"use client";

import React from "react";
import styles from "./WhyAmity.module.scss";
import { useModalDispatch } from "@/context/ModalContext";
import UnivoCarousel from "@/components/common/UnivoCarousel";
import { IModalActions, ModalTypes } from "@/types/modal";
import cn from "clsx";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue, updateStateSession } from "@/context/utils";
import UnivoImage from "@/components/common/UnivoImage";
import { imageCdn } from "@/constants/images";
import { scheduleAfterPaint } from "@/helpers/scheduleAfterPaint";

const cardData = [
  {
    icon: imageCdn("_s/anywhere_8e152fcbb5.webp"),
    title: "Learn Your Way. Anywhere.",
    subtitle: "Flexible. Structured. Effective.",
    desc: "Study in formats that suit you — videos, visuals, interactive exercises — designed for faster understanding and real-world application",
  },
  {
    icon: imageCdn("_s/ai_e8ccb2bd76.webp"),
    title: "Smarter Learning, Powered by AI",
    subtitle: "Everything You Need. One Platform.",
    desc: "AI summaries, smart practice questions, built-in note tools — all in one intuitive,immersive learning environment built to keep you ahead.",
  },
  {
    icon: imageCdn("_s/anywhere_8e152fcbb5.webp"),
    title: "24/7 AI Academic Support",
    subtitle: "Never Study Alone.",
    desc: "Prof. AMI 3.0 guides you through assignments, clears doubts instantly, and keeps you on track — anytime, anywhere.",
  },
  {
    icon: imageCdn("_s/learn_b941d1da70.webp"),
    title: "Learn by Doing",
    subtitle: "Live Industry Projects. Real Impact.",
    desc: "Solve real business challenges with industry experts. Earn project certifications and build a recruiter-ready portfolio before you graduate.",
  },
  {
    icon: imageCdn("_s/connected_2e25cb825e.webp"),
    title: "Beyond Online. Truly Connected",
    subtitle: "Pan-India Campus Access & Offline Events.",
    desc: "Attend orientations, meetups, and convocation across Amity campuses — combining digital flexibility with real-world community.",
  },
  {
    icon: imageCdn("_s/colab_9a3422419f.webp"),
    title: "Graduate With Future-Ready Skills",
    subtitle: "AI & Industry Certifications.",
    desc: "Earn in-demand credentials to strengthen your CV from day one. Boost your skills with Certification in AI — embedded into your degree.",
  },
  {
    icon: imageCdn("_s/globe_33dace0a4f.webp"),
    title: "Turn Learning Into Opportunity",
    subtitle: "AI-Powered Career Discovery Platform.",
    desc: "From resume building and mock interviews to job search tools — get structured, career support that moves you faster toward placements.",
  },
  {
    icon: imageCdn("_s/community_5429c72b13.webp"),
    title: "A Campus That Lives Online",
    subtitle: "beSocial: Your Virtual Student Hub.",
    desc: "Network, join clubs, attend events, and build meaningful connections in a vibrant online-first community.",
  },
];
const WhyAmity: React.FC<{
  associatedPrograms: any;
  isAffiliatePage?: boolean;
  isProgramListPage?: boolean;
  isLandingPage?: boolean;
}> = ({
  associatedPrograms,
  isAffiliatePage = false,
  isProgramListPage = false,
  isLandingPage = false,
}) => {
  const modalDispatch = useModalDispatch();

  const handleBtnClicked = () => {
    if(isLandingPage){
      modalDispatch({
        type: IModalActions.OpenModal,
        data: {
          modalType: ModalTypes.ConnectModalMba,
          modalData: {
            heading: "Join India's No. 1 Online University",
            submitBtnText: "Apply Now",
            associatedPrograms,
            isLambdaAPI: true,
            rfiCase: "THANK_YOU_PAGE_CASE"
          },
        },
      });
    } else{
       modalDispatch({
      type: IModalActions.OpenModal,
      data: {
        modalType: ModalTypes.ConnectModalMobileNumber,
        modalData: {
          heading: "Join India's No. 1 Online University",
          submitBtnText: "Submit",
          isLandingPage: true,
          isAffiliatePage,
          associatedPrograms,
          rfiCase: "REDIRECT_APPLICATION_JOURNEY",
        },
      },
    });
    }
   

    // Same events/payloads — after modal open paints (keeps open-click INP low).
    scheduleAfterPaint(() => {
      pushToDataLayer({
        event: "lead_initiate",
        cta_text: "Start Your Learning Journey",
        section_name: "Why Say Yes to Amity Online",
        page_type: getGlobalStateValue("pageType"),
        page_url: window.location.href,
      });

      updateStateSession({
        parentSection: "Why Say Yes to Amity Online",
        leadStartingCta: "Start Your Learning Journey",
      });

      window?.webengage?.track("Start Your Learning Journey Clicked", {
        "Page Type": getGlobalStateValue("pageType"),
      });
    });
  };

  return (
    <section className={styles.wrapper} id="why_us">
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.mainTitle}>Why Say Yes to Amity Online</h2>
            <p className={styles.tagline}>
              Globally recognized | Digitally advanced | Deeply personalized
            </p>
          </div>

          {!isProgramListPage && (
            <button className={styles.topCta} onClick={handleBtnClicked}>
              Start Your Learning Journey
            </button>
          )}
        </header>
        <hr className={styles.divider} />
      </div>

      <div className={styles.carousel_container}>
        <UnivoCarousel
          preset="whyAmity"
          className={styles.carousel}
          settings={{
            navigation: {
              status: false,
              nextEl: styles.nextBtn,
              prevEl: styles.prevBtn,
            },
          }}
        >
          {cardData.map((card, index) => (
            <div key={index} className={styles.card_container}>
              <div className={styles.card}>
                <div className={styles.cardTop}>
                  <UnivoImage
                    src={card.icon}
                    alt={card.title}
                    className={styles.icon}
                    width={48}
                    height={48}
                    sizes="48px"
                    loading="lazy"
                    fetchPriority="low"
                  />
                  <h3 className={cn(styles.cardTitle, "lining-nums")}>
                    {card.title}
                  </h3>
                </div>

                <div className={styles.cardBody}>
                  <h4 className={styles.cardSubtitle}>{card.subtitle}</h4>
                  <p className={styles.cardDesc}>{card.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </UnivoCarousel>
      </div>
    </section>
  );
};

export default WhyAmity;
